import { FocusScope } from "@react-aria/focus"
import PropTypes from "prop-types"
import React from "react"
import { connect } from "react-redux"
import styled from "styled-components"

import ErrorGroup from "areas/product/error-group"
import { updateChoice, setSignatureBasket } from "areas/product/store/actions"
import { Box, Typography } from "components/common"
import { assetRequire } from "helpers/asset"
import { to_currency } from "helpers/number"
import limitChars from "helpers/product/limit-chars"
import { filterProductOptions } from "helpers/product/product-filtering"
import { useNavigation } from "helpers/use-navigation"

import {
  ChoiceOption,
  OptionsSlider,
  OptionTitle,
  OptionPrice,
  SkuIcon,
} from "./components"

const SectionTitle = styled.h3`
  color: ${(props) => props.theme.colors.black};
  font-size: 14px;
  font-weight: 700;
  font-weight: 700;
  letter-spacing: 0.5px;
  letter-spacing: 0;
  margin: 0 0 5px;
`

const DiamondSettingSide = styled(Box)`
  display: inline-block;
  margin-bottom: 20px;
  margin-right: 25px;
  text-align: center;
`

const DiamondSettingImage = styled(Box).attrs({
  as: "img",
})`
  height: 75px;
  margin-bottom: 5px;
  width: 75px;
`

const DiamondSideSelect = styled(Box).attrs({
  as: "select",
})`
  ${({ theme }) => `
    background-color: ${theme.colors.white};
    border-radius: 0;
    display: block;
    margin-top: 10px;
    width: 100%;
  `}
`
const SkuOptions = ({ mo, setChoice, data, selectedOpt }) => {
  const keyboardProps = useNavigation({ direction: "horizontal" })

  return (
    <ChoiceOption
      aria-checked={selectedOpt}
      data-cy={`product-wizard-option-${data.pref}-${mo.value}`}
      onClick={(e) => setChoice(data.pref, mo, e)}
      role="radio"
      selected={selectedOpt}
      tabIndex={selectedOpt ? 0 : -1}
      {...keyboardProps}
    >
      <SkuIcon skuColor={mo.value === "VERMY" ? "V" : mo.value.slice(-1)}>
        {mo.value === "VERMY" ? "V" : mo.icon_text}
      </SkuIcon>
      <OptionTitle>{mo.title}</OptionTitle>
      <OptionPrice>
        {mo.price > 0 ? "(+" + to_currency(mo.price) + ")" : ""}
      </OptionPrice>
    </ChoiceOption>
  )
}
SkuOptions.propTypes = {
  data: PropTypes.object,
  mo: PropTypes.object,
  selectedOpt: PropTypes.bool,
  setChoice: PropTypes.func,
}

const ChoiceOptions = ({
  signatureOn,
  signatureOff,
  signatureBasket,
  data,
}) => {
  const keyboardProps = useNavigation({ direction: "horizontal" })

  return (
    <OptionsSlider aria-label="Setting Options" role="radiogroup">
      <ChoiceOption
        aria-selected={!signatureBasket}
        onClick={(e) => {
          signatureOff(e)
        }}
        role="radio"
        selected={!signatureBasket}
        tabIndex={!signatureBasket ? 0 : -1}
        {...keyboardProps}
      >
        <img
          height="75"
          src={assetRequire(
            `images/product/diamond/baskets/${data.base_basket.image_code}-sketch.png`
          )}
          width="75"
        />
        <OptionTitle>{data.base_basket.title}</OptionTitle>
      </ChoiceOption>
      <ChoiceOption
        onClick={(e) => {
          signatureOn(e)
        }}
        selected={signatureBasket}
        {...keyboardProps}
      >
        <img
          height="75"
          src={assetRequire(
            `images/product/diamond/baskets/${data.signature_basket.image_code}-sketch.png`
          )}
          width="75"
        />
        <OptionTitle>Signature Setting</OptionTitle>
      </ChoiceOption>
    </OptionsSlider>
  )
}

ChoiceOptions.propTypes = {
  data: PropTypes.object,
  signatureBasket: PropTypes.string,
  signatureOff: PropTypes.func,
  signatureOn: PropTypes.func,
}

const InternalAdditionalMetalOptions = ({
  data,
  extraValues,
  errorText,
  filterParams,
  selectedValue,
  setChoice,
  handleSignatureChange,
  signatureBasket,
  signatureOn,
  signatureOff,
}) => {
  if (!data) {
    return null
  }
  // const filterKeys = Object.keys(filterParams)

  let out = [
    <FocusScope key="sku-options">
      <OptionsSlider
        aria-label={`${data.heading.title} Options`}
        key="base"
        role="radiogroup"
      >
        {filterProductOptions(data.options, filterParams, true).map((mo) => {
          const selectedOpt = mo.value == selectedValue
          return (
            <SkuOptions
              data={data}
              key={`sliderMetalOption-${mo.value}`}
              mo={mo}
              selectedOpt={selectedOpt}
              setChoice={setChoice}
            />
          )
        })}
      </OptionsSlider>
    </FocusScope>,
  ]

  if (data.settings_metal && !data.is_signature) {
    out.push(
      <div key="default-sig">
        <SectionTitle>Select a Setting</SectionTitle>
        <Typography mb="12px" mt="0">
          Choose between the {data.base_basket.title} or select the{" "}
          <strong>Jewlr Signature Setting</strong>. Add yours and your loved
          one&apos;s initials for an added personal touch.
        </Typography>
        <FocusScope>
          <ChoiceOptions
            data={data}
            signatureBasket={signatureBasket}
            signatureOff={signatureOff}
            signatureOn={signatureOn}
          />
        </FocusScope>
      </div>
    )
  }

  if (data.settings_metal && signatureBasket) {
    let metalString = "sketch"
    switch (selectedValue[selectedValue.length - 1]) {
      case "R":
        metalString = "rose"
        break
      case "C":
        metalString = "Ceramic"
        break
      case "Y":
        metalString = "yellow"
        break
      case "W":
        metalString = "white"
        break
      default:
        metalString = "sketch"
    }
    const initial1 = data.initials.find(
      (initial) => initial.value == extraValues.e1.value
    )
    const initial2 = data.initials.find(
      (initial) => initial.value == extraValues.e2.value
    )
    out.push(
      <div key="sig">
        <SectionTitle>Choose a letter for each side</SectionTitle>

        <DiamondSettingSide>
          {!initial1 ? (
            <DiamondSettingImage
              src={require("images/product/diamond/diamond_setting.png")}
            />
          ) : (
            <DiamondSettingImage
              src={assetRequire(
                `images/product/diamond/baskets/basket-a-${extraValues.e1.value.toLowerCase()}-${metalString}.png`
              )}
            />
          )}
          <Typography as="h5" m="0 0 5px">
            SIDE 1
          </Typography>

          <DiamondSideSelect
            onChange={(e) => {
              handleSignatureChange("e1", e)
            }}
            value={extraValues.e1.value}
          >
            <option value="">Select</option>
            {data.initials.map((initial) => (
              <option
                key={`initial-side1-${initial.value}`}
                value={initial.value}
              >
                {initial.value}
              </option>
            ))}
          </DiamondSideSelect>
        </DiamondSettingSide>
        <DiamondSettingSide>
          {!initial2 ? (
            <DiamondSettingImage
              src={require("images/product/diamond/diamond_setting.png")}
            />
          ) : (
            <DiamondSettingImage
              src={assetRequire(
                `images/product/diamond/baskets/basket-a-${extraValues.e2.value.toLowerCase()}-${metalString}.png`
              )}
            />
          )}
          <Typography as="h5" m="0 0 5px">
            SIDE 2
          </Typography>
          <DiamondSideSelect
            onChange={(e) => {
              handleSignatureChange("e2", e)
            }}
            value={extraValues.e2.value}
          >
            <option value="">Select</option>
            {data.initials.map((initial) => (
              <option
                key={`initial-side2-${initial.value}`}
                value={initial.value}
              >
                {initial.value}
              </option>
            ))}
          </DiamondSideSelect>
        </DiamondSettingSide>
      </div>
    )
  }

  return (
    <Box minHeight="55px" px="15px">
      {errorText && <ErrorGroup>{errorText}</ErrorGroup>}
      {out}
    </Box>
  )
}

InternalAdditionalMetalOptions.propTypes = {
  selectedValue: PropTypes.string,
}

const getExtraValues = (state, ownProps) => {
  if (
    state.wizard.data[ownProps.pref] &&
    state.wizard.data[ownProps.pref].settings_metal
  ) {
    if (typeof state.choices === "undefined") {
      return { e1: { value: "" }, e2: { value: "" }, s2: { value: "" } }
    } else {
      return {
        e1: state.choices.e1 ? state.choices.e1 : { value: "" },
        e2: state.choices.e2 ? state.choices.e2 : { value: "" },
        s2: state.choices.s2 ? state.choices.s2 : { value: "" },
      }
    }
  } else {
    return { e1: { value: "" }, e2: { value: "" }, s2: { value: "" } }
  }
}

const getFilterParams = (state, ownProps) => {
  let filterParams = {}
  if (state.choices && state.choices[ownProps.pref]) {
    filterParams = {
      pref: "sku",
      value: state.choices["sku"].value || "",
    }
  }
  return filterParams
}

const getSignatureBasket = (state) => {
  return state.diamondSelector.signatureBasket
}

const getData = (state, ownProps) => {
  return state.wizard.data[ownProps.pref]
}

const getErrorText = (state, ownProps) => {
  if (typeof state.choices[ownProps.pref] === "undefined") {
    return null
  } else {
    return state.choices[ownProps.pref].error
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    data: getData(state, ownProps),
    e1data: state.wizard.data ? state.wizard.data.e1 : {},
    e2data: state.wizard.data ? state.wizard.data.e2 : {},
    errorText: getErrorText(state, ownProps),
    extraValues: getExtraValues(state, ownProps),
    filterParams: getFilterParams(state, ownProps),
    selectedValue: state.choices[ownProps.pref]
      ? state.choices[ownProps.pref].value || ""
      : "",
    signatureBasket: getSignatureBasket(state),
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    dispatch: dispatch,
    setChoice: (pref, data, e) => {
      dispatch(updateChoice(pref, data, true))
    },
    signatureOff: () => {
      dispatch(updateChoice("e1", { disabled: true }))
      dispatch(updateChoice("e2", { disabled: true }))
      dispatch(updateChoice("s2", { disabled: true }))
      dispatch(setSignatureBasket(false))
    },
  }
}

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const { extraValues, e1data, e2data } = stateProps
  const { dispatch } = dispatchProps

  return {
    ...ownProps,
    ...stateProps,
    ...dispatchProps,
    handleSignatureChange: (pref, e) => {
      const engraving = Object.assign({}, stateProps[pref + "data"], {
        disabled: false,
        value: e.target.value,
      })
      limitChars(engraving, e, dispatch)
    },
    signatureOn: () => {
      dispatch(
        updateChoice(
          "e1",
          Object.assign({}, e1data, {
            disabled: false,
            value: extraValues.e1.value || "",
          })
        )
      )
      dispatch(
        updateChoice(
          "e2",
          Object.assign({}, e2data, {
            disabled: false,
            value: extraValues.e2.value || "",
          })
        )
      )
      dispatch(
        updateChoice(
          "s2",
          Object.assign({}, e2data, {
            disabled: false,
            value: extraValues.s2.value || "",
          })
        )
      )
      dispatch(setSignatureBasket(true))
    },
  }
}

InternalAdditionalMetalOptions.propTypes = {
  data: PropTypes.object,
  errorText: PropTypes.string,
  extraValues: PropTypes.object,
  filterParams: PropTypes.object,
  handleSignatureChange: PropTypes.func,
  setChoice: PropTypes.func,
  signatureBasket: PropTypes.bool,
  signatureOff: PropTypes.func,
  signatureOn: PropTypes.func,
}

const AdditionalMetalOptions = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps
)(InternalAdditionalMetalOptions)

export default AdditionalMetalOptions
